/**
 * A generic wrapper for the native fetch API.
 * * @param {string} endpoint - The URL to fetch (can be relative or absolute).
 * @param {object} options - Configuration options.
 * @param {string} [options.method='GET'] - HTTP method (GET, POST, PUT, DELETE).
 * @param {object} [options.body] - The data to send (automatically stringified).
 * @param {string} [options.token] - The auth token (automatically added to headers).
 * @param {object} [options.headers] - Custom headers to merge or override defaults.
 */
import { getApiBaseUrl } from "../../utils/apiUtils";

export const callAPI = async (
  endpoint,
  { method = "GET", body, token, headers = {}, ...customConfig } = {}
) => {
  // 1. Prepare Headers
  const configHeaders = {
    "Content-Type": "application/json",
    ...headers, // Allow overriding Content-Type if needed
  };

  if (token) {
    configHeaders.Authorization = `Token ${token}`;
  }

  // 2. Configure the Request
  const config = {
    method,
    headers: configHeaders,
    ...customConfig, // Allow passing other fetch options like 'mode', 'cache', etc.
  };

  // 3. Handle Body (only if data is provided)
  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${getApiBaseUrl()}/${endpoint}`, config);

    // 4. Handle Response Errors (Fetch doesn't throw on 400-500 status)
    if (!response.ok) {
      // Try to parse error message from server, fallback to status text
      const errorData = await response.json().catch(() => null);
      const errorMessage =
        errorData?.message ||
        `API Error: ${response.status} ${response.statusText}`;
      throw new Error(errorMessage);
    }

    // 5. Return parsed JSON
    // Note: If you expect empty responses (204), you might need to handle this differently
    return await response.json();
  } catch (error) {
    // Log error or handle global error reporting here
    console.error("API Call Failed:", error);
    throw error; // Re-throw so the calling component knows it failed
  }
};
